import { IMAGE_CONFIG, IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { Directive, ElementRef, Renderer2, effect, inject, input, untracked } from '@angular/core';

const PLACEHOLDER_BLUR_AMOUNT = 15;

@Directive({
    selector: '[csBgSrc]',
    standalone: true,
})
export class BackgroundImageLoader {
    csBgSrc = input.required<string>();
    fallbackSrc = input<string>('');
    private imageLoader = inject(IMAGE_LOADER);
    private imageConfig = inject(IMAGE_CONFIG);
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);

    constructor() {
        effect(() => {
            if (!this.csBgSrc()) {
                return;
            }
            untracked(() => {
                this.loadPlaceholderImage();
                this.loadImage();
            });
        });
    }

    private loadPlaceholderImage() {
        const config: ImageLoaderConfig = { src: this.csBgSrc(), width: this.imageConfig.placeholderResolution, isPlaceholder: true };
        const placeholderUrl = this.getImageUrl(config);
        this.setBackground(placeholderUrl, true);
    }

    private getImageUrl(config: ImageLoaderConfig) {
        try {
            return this.imageLoader(config);
        } catch {
            return this.fallbackSrc();
        }
    }

    private loadImage() {
        const config: ImageLoaderConfig = { src: this.csBgSrc() };
        const optimizedSrc = this.getImageUrl(config);
        const img = this.renderer.createElement('img');
        this.renderer.setAttribute(img, 'fetchPriority', 'high');
        this.renderer.setAttribute(img, 'loading', 'eager');
        this.renderer.setAttribute(img, 'src', optimizedSrc);
        img.onload = () => {
            this.setBackground(optimizedSrc);
        };
        img.onerror = () => {
            this.setBackground(this.fallbackSrc());
        };
    }

    private setBackground(imageUrl: string, isPlaceholder = false) {
        this.renderer.setStyle(this.el.nativeElement, 'background-image', `url('${imageUrl}')`);
        if (isPlaceholder) {
            this.renderer.setStyle(this.el.nativeElement, 'filter', `blur(${PLACEHOLDER_BLUR_AMOUNT}px)`);
        } else {
            this.renderer.setStyle(this.el.nativeElement, 'filter', 'none');
        }
    }
}
